import * as PIXI from 'pixi.js';
import * as PrefabManager from '../PrefabManager';
import * as AudioManager from '../../utils/AudioManager';
import * as emitterManager from '../../utils/EmitterManager';
import { disableCulling } from '../../utils/PIXICuller';
import { setStrokeStyle } from '../../utils/pixiGraphics';
import { Settings } from '../../Settings';
import {
	game
} from "../../Game";
import { advanceCut, beamIntervals, beamToLocal, bodyShapes, cutState, isFullyCut, newTrack } from './laserTracking';
import { cuttableKind, isFrontLayer, partDamage, splitCut } from './laserCut';
import { bleedWhileCutting, scarPartialCut } from './laserGore';
import { scorch } from '../../utils/paint/scorch';
import { hasLinkedPortals } from '../guns/PortalBridge';
import { beamPath } from './laserPath';

const { getPointer } = Box2D;

// Where the beam leaves the magenta lens of the gun art, along its axis.
const LENS = 1.8;
const QUERY_PADDING = 2;
// Back to front: a soft glow, the lens colour of the gun art, a lighter band and a white core.
const BAND_WIDTH = 18;
const BEAM_LAYERS = [
	[44, 0xff00ff, 0.06], [36, 0xff00ff, 0.1], [29, 0xff00ff, 0.14], [23, 0xff00ff, 0.22],
	[BAND_WIDTH, 0xff00ff, 1], [12, 0xff8cff, 1], [7, 0xffffff, 1],
];
const SPARKS_EVERY = 4;
// Physics steps the beam needs to burn through a turret.
const TURRET_BURN_STEPS = 10;
// Steps between the burn marks the beam leaves where it stops on something.
const BURN_MARK_STEPS = 3;
// How far in front of the lens the bulb at the start of the beam sits.
const LENS_BULB = 0.3;

const vec1 = new Box2D.b2Vec2();
const vec2 = new Box2D.b2Vec2();

const beamPoint = (beam, t) => ({x: beam.x + beam.dx * t, y: beam.y + beam.dy * t});

const blocksBeam = (fixture, ignoreKey) => {
	if(fixture.IsSensor()) return false;
	if(fixture.GetFilterData().get_categoryBits() === game.editor.MASKBIT_NOTHING) return false;
	const body = fixture.GetBody();
	if(body.isPhysicsCamera || body.isPortalGhost || body.isPortalShadow) return false;
	if(body.isHumanoid || body.isVain || body.isHat || body.depthCut) return false;
	const sprite = body.mySprite;
	if(!sprite || sprite.isPhysicsParticleSprite || sprite.data?.type === game.editor.object_TRIGGER) return false;
	if(sprite.data?.prefabInstanceName === ignoreKey) return false;
	if(cuttableKind(body)) return false;
	// Riders are sliced along with what they sit on; the laser does not cut vehicles.
	const owner = game.editor.retrieveClassFromBody(body);
	return !owner?.isVehicle && !owner?.character;
};

const rayCallback = Object.assign(new Box2D.JSRayCastCallback(), {
	ignoreKey: null,
	fraction: 1,
	hit: null,
	reset(ignoreKey) {
		this.ignoreKey = ignoreKey;
		this.fraction = 1;
		this.hit = null;
	},
	ReportFixture(fixture_p, point_p, normal_p, fraction) {
		const fixture = Box2D.wrapPointer(fixture_p, Box2D.b2Fixture);
		if(!blocksBeam(fixture, this.ignoreKey)) return -1;
		if(fraction < this.fraction){
			this.fraction = fraction;
			this.hit = fixture.GetBody();
		}
		return fraction;
	},
});

const queryCallback = Object.assign(new Box2D.JSQueryCallback(), {
	bodies: [],
	pointers: new Set(),
	reset() {
		this.bodies.length = 0;
		this.pointers.clear();
	},
	ReportFixture(fixture_p) {
		const body = Box2D.wrapPointer(fixture_p, Box2D.b2Fixture).GetBody();
		const pointer = getPointer(body);
		if(this.pointers.has(pointer)) return true;
		this.pointers.add(pointer);
		if(body.destroyed || !body.mySprite || body.isPortalGhost || body.isPortalShadow) return true;
		if(isFrontLayer(body) || cuttableKind(body)) this.bodies.push(body);
		return true;
	},
});

class Laser extends PrefabManager.weaponPrefab {
	static settings = Object.assign({}, PrefabManager.weaponPrefab.settings, {
		"isFixed": true,
		"active": true,
		"laserRange": 30,
		"blockable": true,
	});
	static settingsOptions = Object.assign({}, PrefabManager.weaponPrefab.settingsOptions, {
		"isFixed": true,
		"active": true,
		"laserRange": {
			min: 2,
			max: 150,
			step: 1
		},
		"blockable": true,
	});

	constructor(target) {
		super(target);
		this.prefabObject.settings = Object.assign({}, Laser.settings, this.prefabObject.settings);
		this.isLaser = true;
	}

	init() {
		super.init();
		this.base = this.lookupObject.base;
		this.step = 0;
		this.tracks = [];
		this.beams = [];
		this.cutAt = null;
		this.depthLayers = [];
		this.wasActive = this.prefabObject.settings.active;
		this.flash = 0;

		this.effects = new PIXI.Container();
		this.effects.isLaserBeam = true;
		this.graphics = new PIXI.Graphics();
		this.effects.addChild(this.graphics);
		disableCulling(this.effects);
		game.editor.textures.addChild(this.effects);

		// Sparks fly back off whatever the beam hits.
		this.sparks = emitterManager.getLoopingEmitter('sparksMetal', null, this.base.GetPosition(), 0);
		this.sparks.parent = this.effects;
		this.sparks.setColors('#ffffff', '#ff00ff');
		this.sparks.minStartRotation = -55;
		this.sparks.maxStartRotation = 55;

		AudioManager.preloadSFX('laser_beam_loop');
		// Cut fruit plays its own break sound.
		AudioManager.preloadSFX('fruit-explode');
	}

	update() {
		super.update();
		if(this.destroyed || !this.base || this.base.destroyed || !this.effects) return;
		this.step++;
		this.cutAt = null;

		const active = this.prefabObject.settings.active;
		if(active !== this.wasActive){
			this.wasActive = active;
			this.flash = active ? 1 : 0;
			AudioManager.playSFX(active ? 'laser_on' : 'laser_off', 0.5, 1, this.base.GetPosition());
		}
		if(active){
			this.castBeam();
			this.sweep();
			this.burnTurret();
			this.burnSurface();
		}else{
			this.beams = [];
			this.tracks.length = 0;
			this.burnTarget = null;
		}
		this.updateSound();
		this.draw();
		this.sparks.update(game.editor.deltaTime / 1000);
	}

	// The beam from the lens, carrying on through linked portals it enters.
	castBeam() {
		const settings = this.prefabObject.settings;
		const position = this.base.GetPosition();
		const angle = this.base.GetAngle();
		const dx = Math.cos(angle);
		const dy = Math.sin(angle);
		const x = position.x + dx * LENS;
		const y = position.y + dy * LENS;
		const range = settings.laserRange;
		if(!hasLinkedPortals()){
			let length = range;
			let hit = null;
			if(settings.blockable){
				vec1.Set(x, y);
				vec2.Set(x + dx * range, y + dy * range);
				rayCallback.reset(this.prefabObject.key);
				game.world.RayCast(rayCallback, vec1, vec2);
				length = range * rayCallback.fraction;
				hit = rayCallback.hit;
			}
			this.beams = [{x, y, dx, dy, length, blocked: length < range, hit}];
			return;
		}
		const key = this.prefabObject.key;
		this.beams = beamPath(game.world, x, y, dx, dy, range, fixture => settings.blockable && blocksBeam(fixture, key));
	}

	lastBeam() {
		return this.beams[this.beams.length - 1];
	}

	burnSurface() {
		const beam = this.lastBeam();
		if(!beam.hit || this.step % BURN_MARK_STEPS) return;
		const end = beamPoint(beam, beam.length);
		scorch(beam.hit, end.x, end.y, 18 + Math.random() * 12, Math.random() * Math.PI * 2, 0.45);
	}

	burnTurret() {
		const hit = this.lastBeam().hit;
		const turret = hit && game.editor.retrieveClassFromBody(hit);
		if(!turret?.isTurret || turret.broken || !turret.prefabObject.settings.isDestructible){
			this.burnTarget = null;
			return;
		}
		if(turret !== this.burnTarget){
			this.burnTarget = turret;
			this.burnSteps = 0;
		}
		if(++this.burnSteps === TURRET_BURN_STEPS) turret.queueBreak();
	}

	// Cut progress only advances where a part crossed the beam between two
	// physics steps, so slow dips, fast passes and sweeping lasers all count.
	// Each stretch of the beam keeps its own tracks, so a part near both portals
	// is swept by each stretch separately.
	sweep() {
		const splits = new Set();
		this.beams.forEach((beam, i) => {
			const tracks = this.tracks[i] || (this.tracks[i] = new Map());
			const end = beamPoint(beam, beam.length);
			const aabb = new Box2D.b2AABB();
			aabb.get_lowerBound().Set(Math.min(beam.x, end.x) - QUERY_PADDING, Math.min(beam.y, end.y) - QUERY_PADDING);
			aabb.get_upperBound().Set(Math.max(beam.x, end.x) + QUERY_PADDING, Math.max(beam.y, end.y) + QUERY_PADDING);
			queryCallback.reset();
			game.world.QueryAABB(queryCallback, aabb);
			Box2D.destroy(aabb);

			beam.front = [];
			for(const body of queryCallback.bodies.slice()){
				const kind = cuttableKind(body);
				if(kind && this.sweepBody(body, kind, beam, tracks)) splits.add(body);
				if(isFrontLayer(body)) beam.front.push(body);
			}
			tracks.forEach((track, body) => {
				if(track.step !== this.step) tracks.delete(body);
			});
		});
		this.tracks.length = this.beams.length;
		splits.forEach(body => {
			if(cuttableKind(body)) splitCut(body);
		});
	}

	sweepBody(body, kind, beam, tracks) {
		const state = cutState(body, kind);
		const local = beamToLocal(body, beam);

		let track = tracks.get(body);
		if(!track || track.state !== state || track.step !== this.step - 1){
			track = newTrack(state);
			tracks.set(body, track);
		}
		const swept = advanceCut(track, local, beam.length);
		track.step = this.step;

		const intervals = beamIntervals(state.shapes, local, beam.length);
		const inBeam = intervals.length > 0;
		if(inBeam && !this.cutAt) this.cutAt = beamPoint(beam, intervals[0][0]);

		if(swept > 0 && inBeam){
			const [t0, t1] = intervals[0];
			state.frontier = {
				x0: local.qx + local.dx * t0, y0: local.qy + local.dy * t0,
				x1: local.qx + local.dx * t1, y1: local.qy + local.dy * t1,
			};
		}

		// Fruit is soft enough to fall apart the moment the beam touches it.
		const fullyCut = isFullyCut(state) || (state.kind === 'fruit' && inBeam);
		if(state.kind === 'humanoid'){
			if(swept > 0) game.editor.retrieveSubClassFromBody(body)?.dealDamage(partDamage(body, swept));
			if(inBeam) bleedWhileCutting(body, beam, intervals, this.step);
			if(track.inBeam && !inBeam && !fullyCut && state.count > 0) scarPartialCut(body, state);
		}
		track.inBeam = inBeam;
		return fullyCut;
	}

	// Plays a loop at a point, or stops it for no point.
	loop(key, name, volume, pitch, point) {
		if(!point){
			AudioManager.stopPrefabUniqueLoopSFX(key, name);
			return;
		}
		vec1.Set(point.x, point.y);
		AudioManager.playPrefabUniqueLoopSFX(key, name, volume, pitch, vec1);
	}

	// A hum at the lens, and a sizzle where the beam cuts and where it burns on something.
	updateSound() {
		const last = this.lastBeam();
		const key = this.prefabObject.key;
		this.loop(key, 'laser_beam_loop', 0.25, 1, this.beams[0]);
		this.loop(`${key}:cut`, 'laser_burn_loop', 0.8, 1, this.cutAt);
		this.loop(`${key}:hit`, 'laser_burn_loop', 0.35, 1.2, last?.blocked ? beamPoint(last, last.length) : null);
	}

	keepOnTop() {
		const textures = game.editor.textures;
		const children = textures.children;
		for(let i = children.length - 1; i >= 0 && children[i] !== this.effects; i--){
			if(!children[i].isLaserBeam){
				textures.setChildIndex(this.effects, children.length - 1);
				return;
			}
		}
	}

	// The beam runs through the middle of everything's depth. Each stretch of it
	// goes into the display list right under the lowest art in front of it there,
	// so heads, torsos, near limbs, eyes, hats and halves that come off cover it.
	// Each layer, from the soft glow to the white core, is drawn whole before the
	// next, so nothing breaks the white line and circles on top.
	draw() {
		this.graphics.clear();
		this.depthLayers.forEach(layer => {
			layer.clear();
			layer.parent?.removeChild(layer);
		});
		this.flash *= 0.85;
		if(!this.beams.length) return;
		this.keepOnTop();

		const PTM = Settings.PTM;
		const pieces = new Map();
		const pool = {used: 0};
		const first = this.beams[0];
		const last = this.lastBeam();
		this.beams.forEach(beam => {
			const point = t => [(beam.x + beam.dx * t) * PTM, (beam.y + beam.dy * t) * PTM];
			const own = this.drawStretches(beam, pieces, pool).map(({t0, t1, piece}) => {
				piece.spans.push([...point(t0), ...point(t1)]);
				return {t0, t1, piece};
			});
			// Bulbs where the beam leaves the lens and where it ends.
			const bulb = (t, radius) => own.find(({t0, t1}) => t0 <= t && t <= t1).piece.bulbs.push([...point(t), radius]);
			if(beam === first) bulb(Math.min(LENS_BULB, beam.length), 15 * (1 + 0.06 * Math.sin(this.step * 0.3) + this.flash));
			if(beam === last) bulb(beam.length, beam.blocked ? 12 * (0.95 + Math.random() * 0.1) : BAND_WIDTH / 2);
		});

		pieces.forEach(({spans, bulbs}, target) => {
			BEAM_LAYERS.forEach(([width, color, alpha]) => {
				spans.forEach(([x0, y0, x1, y1]) => target.moveTo(x0, y0).lineTo(x1, y1).stroke({width, color, alpha}));
				bulbs.forEach(([x, y, radius]) => target.circle(x, y, radius + (width - BAND_WIDTH) / 2).fill({color, alpha}));
			});
		});

		if(last.blocked && this.step % SPARKS_EVERY === 0){
			const end = beamPoint(last, last.length);
			this.sparks.spawnPos.set(end.x * PTM, end.y * PTM);
			this.sparks.rotation = Math.atan2(-last.dy, -last.dx) * game.editor.RAD2DEG;
			this.sparks.playOnce();
		}
	}

	// One stretch of the beam split where art in front of it starts and ends,
	// each with the piece of drawing it goes into: under the lowest such art.
	drawStretches(beam, pieces, pool) {
		const textures = game.editor.textures;
		const reach = BAND_WIDTH / 2 / Settings.PTM;
		const covers = [];
		beam.front.forEach(body => {
			const texture = body.myTexture;
			if(body.destroyed || texture?.parent !== textures) return;
			const index = textures.getChildIndex(texture);
			beamIntervals(bodyShapes(body, reach), beamToLocal(body, beam), beam.length)
				.forEach(([t0, t1]) => covers.push({t0, t1, texture, index}));
		});
		const stops = [...new Set([0, beam.length, ...covers.flatMap(({t0, t1}) => [t0, t1])])].sort((a, b) => a - b);
		const stretches = [];
		for(let i = 1; i < stops.length; i++){
			const middle = (stops[i - 1] + stops[i]) / 2;
			let under = null;
			covers.forEach(cover => {
				if(middle > cover.t0 && middle < cover.t1 && (!under || cover.index < under.index)) under = cover;
			});
			const last = stretches[stretches.length - 1];
			if(last && last.under?.texture === under?.texture) last.t1 = stops[i];
			else stretches.push({t0: stops[i - 1], t1: stops[i], under});
		}
		if(!stretches.length) stretches.push({t0: 0, t1: beam.length, under: null});

		return stretches.map(stretch => {
			let target = this.graphics;
			if(stretch.under){
				target = [...pieces.keys()].find(layer => layer.under === stretch.under.texture);
				if(!target){
					target = this.depthLayers[pool.used] || this.depthLayer();
					pool.used++;
					target.under = stretch.under.texture;
					textures.addChildAt(target, textures.getChildIndex(target.under));
				}
			}
			if(!pieces.has(target)) pieces.set(target, {spans: [], bulbs: []});
			return {t0: stretch.t0, t1: stretch.t1, piece: pieces.get(target)};
		});
	}

	depthLayer() {
		const layer = new PIXI.Graphics();
		disableCulling(layer);
		this.depthLayers.push(layer);
		return layer;
	}

	drawDebugEditor() {
		const sprite = this.lookupObject?.base?.mySprite;
		if(!sprite) return;
		const editor = game.editor;
		const camera = editor.cameraHolder;
		const length = this.prefabObject.settings.laserRange * Settings.PTM;
		const lens = LENS * Settings.PTM;
		const dx = Math.cos(sprite.rotation);
		const dy = Math.sin(sprite.rotation);
		const toScreen = distance => [
			(sprite.x + dx * distance) * camera.scale.x + camera.x,
			(sprite.y + dy * distance) * camera.scale.y + camera.y,
		];
		setStrokeStyle(editor.debugGraphics, 2, '0xff2020', 0.8);
		editor.debugGraphics.moveTo(...toScreen(lens));
		editor.debugGraphics.lineTo(...toScreen(lens + length));
		editor.debugGraphics.stroke();
	}

	destroy() {
		const key = this.prefabObject?.key;
		if(key){
			AudioManager.stopPrefabUniqueLoopSFX(key, 'laser_beam_loop');
			AudioManager.stopPrefabUniqueLoopSFX(`${key}:cut`, 'laser_burn_loop');
			AudioManager.stopPrefabUniqueLoopSFX(`${key}:hit`, 'laser_burn_loop');
		}
		if(this.sparks){
			emitterManager.destroyEmitter(this.sparks);
			this.sparks = null;
		}
		this.depthLayers?.forEach(layer => layer.destroyed || layer.destroy());
		if(this.effects && !this.effects.destroyed) this.effects.destroy({children: true});
		this.effects = null;
		super.destroy();
	}
}

PrefabManager.prefabLibrary.Laser = {
	json: '{"objects":[[0,0,0,0,"laser","base",0,["#999999"],["#000"],[0],true,true,[[{"x":-2.1333,"y":-0.05},{"x":-1.9333,"y":-0.6167},{"x":-1.4,"y":-0.9167},{"x":2,"y":-0.8833},{"x":2.0667,"y":-0.5833},{"x":1.6667,"y":0.7833},{"x":-1.4333,"y":0.8833},{"x":-1.9667,"y":0.5167}]],[1],[0],[0],"",[0],true,false,false,[0.5],[0.2],false,true,false],[1,0,0,0,"laser","texture",1,"LaserGun0000",0,0,0,0,false,"#FFFFFF",1,1,1,0,0,0,true]]}',
	class: Laser,
	library: PrefabManager.LIBRARY_WEAPON,
}
